import { Info } from "lucide-react";
import { type ReactNode, useState } from "react";

import { NotFoundPage } from "@/features/not-found";
import { useAdminServiceBudgetRequests, useVersion } from "@/lib/api";
import { useUser } from "@/lib/user-context";

import { DecideDialog } from "./decide-dialog";
import { RequestTable } from "./request-table";
import type { PendingDecision } from "./types";

/**
 * The services gateway budget queue: owners ask for a bigger monthly budget
 * for web search, tracing or the models (from the UI or from Claude through
 * MCP), admins decide here. Approving sets the budget in the gateway's key
 * registry straight away; the request itself changes nothing.
 */
export function AdminServiceBudgetRequestsPage() {
  const { user } = useUser();
  const { data: requests = [], isLoading, isError } = useAdminServiceBudgetRequests();
  const [decision, setDecision] = useState<PendingDecision | null>(null);

  const openCount = requests.filter((r) => r.status === "pending" || r.status === "failed").length;

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div>
        <h2 className="text-primary text-2xl font-normal">Budget requests</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Requests for a bigger monthly AI services budget &mdash; {openCount} waiting
        </p>
      </div>

      <div className="border-border bg-muted/40 mt-6 flex items-start gap-2 rounded-lg border p-3">
        <Info className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        <p className="text-muted-foreground text-xs">
          Approving sets the project&apos;s monthly budget for that service at once, as asked or to
          the amount you enter; nothing is redeployed. Web search and tracing count requests, the
          models count US dollars. If the gateway cannot be reached, the request is marked failed
          and you can approve it again.
        </p>
      </div>

      <div className="bg-card border-border mt-6 overflow-hidden rounded-lg border">
        {isLoading ? (
          <div className="text-muted-foreground py-16 text-center text-sm">
            Loading budget requests...
          </div>
        ) : isError ? (
          <div className="text-destructive py-16 text-center text-sm">
            Could not load the budget requests.
          </div>
        ) : (
          <RequestTable
            requests={requests}
            currentUserEmail={user?.email ?? null}
            onDecide={setDecision}
          />
        )}
      </div>

      <DecideDialog
        decision={decision}
        onOpenChange={(open) => {
          if (!open) {
            setDecision(null);
          }
        }}
      />
    </div>
  );
}

/** Renders its page only on an instance with services gateway keys (SVC_GATEWAY_ENABLED). */
export function ServicesGatewayOnly({ children }: { children: ReactNode }) {
  const { data, isLoading } = useVersion();
  if (isLoading) {
    return null;
  }
  return data?.svc_gateway_enabled ? children : <NotFoundPage />;
}
